import { motion, AnimatePresence } from "framer-motion";
import { User, Mic, Palette, Sparkles } from "lucide-react";

interface PersonaData {
    name?: string;
    age?: string;
    style?: string;
    vibe?: string;
    tone?: string;
    [key: string]: string | undefined;
}

interface PersonaRevealProps {
    persona: PersonaData | null;
    isLoading?: boolean;
}

const PERSONA_FIELDS = [
    { key: "name", label: "Nome", icon: User },
    { key: "age", label: "Idade", icon: User },
    { key: "style", label: "Estilo", icon: Palette },
    { key: "vibe", label: "Vibe", icon: Sparkles },
    { key: "tone", label: "Tom de Voz", icon: Mic },
];

export function PersonaReveal({ persona, isLoading }: PersonaRevealProps) {
    if (!persona && !isLoading) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-card to-card/80 border border-border/50 p-6"
        >
            {/* Background glow */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl" />

            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold">Seu Influencer Virtual</h3>
                        <p className="text-xs text-muted-foreground">
                            {isLoading ? "Montando a persona perfeita..." : "Persona criada pela IA"}
                        </p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        {[...Array(4)].map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.15 }}
                                className="flex items-center gap-3"
                            >
                                <div className="w-8 h-8 rounded-lg bg-secondary animate-pulse" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-3 w-16 bg-secondary animate-pulse rounded" />
                                    <div className="h-3 w-32 bg-secondary/50 animate-pulse rounded" />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <AnimatePresence>
                        <div className="space-y-2.5">
                            {PERSONA_FIELDS.map((field, i) => {
                                const value = persona?.[field.key];
                                if (!value) return null;
                                const Icon = field.icon;

                                return (
                                    <motion.div
                                        key={field.key}
                                        initial={{ opacity: 0, x: -15, filter: "blur(4px)" }}
                                        animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                                        transition={{ delay: i * 0.12, duration: 0.5 }}
                                        className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-4 h-4 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                                {field.label}
                                            </p>
                                            <p className="text-sm font-medium leading-tight">{value}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {/* Extra persona fields */}
                            {persona && Object.entries(persona)
                                .filter(([key]) => !PERSONA_FIELDS.some(f => f.key === key) && key !== "raw")
                                .slice(0, 3)
                                .map(([key, value], i) => (
                                    <motion.div
                                        key={key}
                                        initial={{ opacity: 0, x: -15 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: (PERSONA_FIELDS.length + i) * 0.12 }}
                                        className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/20"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                                            <Sparkles className="w-4 h-4 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                                {key.replace(/_/g, " ")}
                                            </p>
                                            <p className="text-sm font-medium leading-tight">{String(value)}</p>
                                        </div>
                                    </motion.div>
                                ))
                            }
                        </div>
                    </AnimatePresence>
                )}
            </div>
        </motion.div>
    );
}
